/**
 * featureFlags.js
 * ---------------------------------------------------------------------------
 * Build-time feature-flag accessors for the React client.
 *
 * Phase C.5 of the looky-loo Guest Mode work introduced the first such flag:
 * `REACT_APP_REGISTRATION_ENABLED`. The original goal was to lock down the
 * deployed WIP preview so internet randos cannot create accounts (which
 * become write-capable identities) while keeping dev fully functional.
 *
 * Convention shared with the server:
 *   - FAIL-OPEN. The flag is a kill-switch, not a permission. Only the
 *     literal string "false" closes the door. Any other value (true, "",
 *     undefined) leaves the feature on. This means dev never has to set
 *     anything explicitly and a missing-env-var deploy doesn't accidentally
 *     lock everyone out.
 *   - The server-side counterpart in userController.registerUser is the
 *     ACTUAL security boundary. The helpers here are UX courtesy — they
 *     hide CTAs that would 503 anyway, so users don't see dead links.
 *
 * Add new flag accessors here as they appear so the predicate logic stays
 * in one place and the inverse of fail-open never spreads by copy-paste.
 * ---------------------------------------------------------------------------
 */

/**
 * Public registration (POST /api/register) — controls whether the
 * Register CTA is rendered in Navbar / LoginPage. The banner-CTA case is
 * handled separately because Phase C.5 dropped that CTA entirely.
 *
 * @returns {boolean} true unless the build was made with
 *   `REACT_APP_REGISTRATION_ENABLED=false` in the active CRA env file.
 */
export function isRegistrationEnabled() {
  // ⛔ FAIL OPEN IN DEV, FAIL CLOSED IN A PRODUCTION BUILD.
  //
  // `!== 'false'` alone means an unset variable advertises registration. That was tolerable when
  // nobody knew the URL; it is the wrong default for a deployed site that voice actors are being
  // invited to, and it fails in the direction that costs something. CRA sets NODE_ENV=production
  // for every `npm run build`, so a built bundle now requires the variable to say 'true' out loud.
  //
  // Same asymmetry as the server's CLIENT_CORS refusal: a value that only matters in production
  // should not be satisfied by silence there.
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_REGISTRATION_ENABLED === 'true';
  }
  return process.env.REACT_APP_REGISTRATION_ENABLED !== 'false';
}
